/**
 * <bk-category-tabs> — переключение табов секции bk-category-tabs.
 *
 * Все панели уже в DOM: скрипт только переставляет класс is-active и ARIA.
 * Запросов к серверу нет. Без JS видна первая панель — она отрендерена
 * активной сразу, поэтому секция остаётся рабочей и при упавшем скрипте.
 *
 * Стрелки — прогрессивное улучшение поверх scroll-snap: пара кнопок в шапке
 * листает ряд активной панели. Без скрипта ряд листается пальцем и колесом,
 * а кнопки не показываются — CSS ждёт data-scrollable="true".
 */
class BkCategoryTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-bk-tab]')];
    this.panels = [...this.querySelectorAll('[data-bk-panel]')];

    this.prev = this.querySelector('[data-bk-prev]');
    this.next = this.querySelector('[data-bk-next]');
    this.prev?.addEventListener('click', this.#handlePrev);
    this.next?.addEventListener('click', this.#handleNext);
    // scroll не всплывает, поэтому слушаем на фазе перехвата.
    this.addEventListener('scroll', this.#updateArrows, { capture: true, passive: true });
    this.resizeObserver = new ResizeObserver(this.#updateArrows);
    this.resizeObserver.observe(this);
    this.#updateArrows();

    if (this.tabs.length < 2) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.#handlePrev);
    this.next?.removeEventListener('click', this.#handleNext);
    this.removeEventListener('scroll', this.#updateArrows, { capture: true });
    this.resizeObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** Ряд видимой панели — единственный, который можно листать. */
  get #row() {
    return this.querySelector('[data-bk-panel].is-active [data-bk-row]');
  }

  #handlePrev = () => this.#step(-1);
  #handleNext = () => this.#step(1);

  /**
   * Шаг прокрутки — ширина одной карточки вместе с промежутком.
   * @param {number} direction -1 назад, 1 вперёд
   */
  #step(direction) {
    const row = this.#row;
    if (!row) return;

    const item = row.firstElementChild;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const distance = item ? item.getBoundingClientRect().width + gap : row.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    row.scrollBy({ left: direction * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #updateArrows = () => {
    const row = this.#row;
    if (!row) return;

    const maxScroll = row.scrollWidth - row.clientWidth;
    this.dataset.scrollable = maxScroll > 1 ? 'true' : 'false';

    if (this.prev) this.prev.disabled = row.scrollLeft <= 1;
    if (this.next) this.next.disabled = row.scrollLeft >= maxScroll - 1;
  };

  #onClick = (event) => {
    const tab = event.target.closest('[data-bk-tab]');
    if (tab && this.tabs.includes(tab)) this.#select(this.tabs.indexOf(tab));
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[data-bk-tab]');
    if (!tab || !this.tabs.includes(tab)) return;

    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;

    event.preventDefault();
    const next = (this.tabs.indexOf(tab) + step + this.tabs.length) % this.tabs.length;
    this.#select(next);
    this.tabs[next].focus();
  };

  #select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });
    // У каждой панели свой ряд и своя позиция прокрутки.
    this.#updateArrows();
  }
}

if (!customElements.get('bk-category-tabs')) {
  customElements.define('bk-category-tabs', BkCategoryTabs);
}
